import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { brl } from '../lib/format';
import { Imovel, StatusImovel, TipoImovel } from '../types';
import { Modal } from '../components/Modal';

const tipos: { v: TipoImovel; l: string }[] = [
  { v: 'comercial', l: 'Ponto comercial' },
  { v: 'casa', l: 'Casa' },
  { v: 'apartamento', l: 'Apartamento' }
];

const statusList: { v: StatusImovel; l: string; badge: string }[] = [
  { v: 'alugado',    l: 'Alugado',    badge: 'badge-green'  },
  { v: 'desocupado', l: 'Desocupado', badge: 'badge-yellow' },
  { v: 'inativo',    l: 'Inativo',    badge: 'badge-slate'  }
];

export const statusBadge = (s: StatusImovel) =>
  statusList.find((x) => x.v === s) ?? statusList[0];

export function Imoveis() {
  const [list, setList] = useState<Imovel[]>([]);
  const [open, setOpen] = useState(false);

  const carregar = () => api.get<Imovel[]>('/imoveis').then(setList);
  useEffect(() => { carregar(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold hidden sm:block">Imóveis</h1>
        <button className="btn-primary" onClick={() => setOpen(true)}>+ Novo imóvel</button>
      </div>

      {list.length === 0 ? (
        <div className="card-pad text-center text-slate-500">
          Nenhum imóvel cadastrado. Clique em "Novo imóvel" para começar.
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-3">
          {list.map((im) => {
            const sb = statusBadge(im.status);
            return (
              <li key={im.id}>
                <Link to={`/imoveis/${im.id}`} className="card-pad block hover:border-brand-500 transition-colors">
                  <div className="flex justify-between gap-2 items-start">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{im.apelido}</div>
                      <div className="text-xs text-slate-500 truncate">{im.endereco}</div>
                    </div>
                    <span className={sb.badge}>{sb.l.toLowerCase()}</span>
                  </div>
                  <div className="mt-3 flex justify-between items-baseline">
                    <span className="text-xs text-slate-500">{tipos.find((t) => t.v === im.tipo)?.l} · {im.dono}</span>
                    <span className="font-semibold">{brl(im.valor_aluguel)}</span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <ImovelForm open={open} onClose={() => setOpen(false)} onSaved={carregar} />
    </div>
  );
}

interface FormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  imovel?: Imovel;
}

export function ImovelForm({ open, onClose, onSaved, imovel }: FormProps) {
  const [form, setForm] = useState({
    apelido: '',
    tipo: 'casa' as TipoImovel,
    endereco: '',
    dono: 'Eu',
    valor_aluguel: 0,
    dia_vencimento: 5,
    observacoes: '',
    status: 'alugado' as StatusImovel
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (imovel) {
      setForm({
        apelido: imovel.apelido,
        tipo: imovel.tipo,
        endereco: imovel.endereco,
        dono: imovel.dono,
        valor_aluguel: imovel.valor_aluguel,
        dia_vencimento: imovel.dia_vencimento,
        observacoes: imovel.observacoes ?? '',
        status: imovel.status
      });
    } else {
      setForm({
        apelido: '', tipo: 'casa', endereco: '', dono: 'Eu',
        valor_aluguel: 0, dia_vencimento: 5, observacoes: '', status: 'alugado'
      });
    }
  }, [imovel, open]);

  const submit = async () => {
    setSalvando(true);
    try {
      if (imovel) await api.put(`/imoveis/${imovel.id}`, form);
      else await api.post('/imoveis', form);
      onSaved();
      onClose();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={imovel ? 'Editar imóvel' : 'Novo imóvel'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={submit} disabled={salvando || !form.apelido}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label">Apelido</label>
          <input className="input" value={form.apelido} onChange={(e) => setForm({ ...form, apelido: e.target.value })} placeholder="ex: Loja Centro" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Tipo</label>
            <select className="input" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoImovel })}>
              {tipos.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Dono</label>
            <input className="input" value={form.dono} onChange={(e) => setForm({ ...form, dono: e.target.value })} placeholder="ex: Eu, Mãe" />
          </div>
        </div>
        <div>
          <label className="label">Endereço</label>
          <input className="input" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Valor do aluguel</label>
            <input type="number" step="0.01" className="input" value={form.valor_aluguel} onChange={(e) => setForm({ ...form, valor_aluguel: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Dia de vencimento</label>
            <input type="number" min={1} max={28} className="input" value={form.dia_vencimento} onChange={(e) => setForm({ ...form, dia_vencimento: Number(e.target.value) })} />
          </div>
        </div>
        <div>
          <label className="label">Observações</label>
          <textarea className="input" rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as StatusImovel })}>
            {statusList.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            Apenas imóveis "alugados" geram recebimento mensal automático.
          </p>
        </div>
      </div>
    </Modal>
  );
}
